import {
    AggregateFunctions,
    AggregateQueryInterval,
    AggregateQueryMode,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    CompositionChartConfig,
    TimeseriesChartConfig,
} from "~/modules/charts/types";
import { MEASURE_SLUGS } from "~/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { ComboboxOptionGroup } from "~/components/ComboboxV2";

// // // //

export const TIMESERIES_GROUP_BY_OPTIONS = [
    { value: TokenTimeseriesGroupBy.protocol, label: "Platform" },
    { value: TokenTimeseriesGroupBy.baseProtocol, label: "Base Protocol" },
    { value: TokenTimeseriesGroupBy.network, label: "Network" },
    { value: TokenTimeseriesGroupBy.parentNetwork, label: "Parent Network" },
    { value: TokenTimeseriesGroupBy.manager, label: "Manager" },
    { value: TokenTimeseriesGroupBy.issuer, label: "Issuer" },
    { value: TokenTimeseriesGroupBy.assetClass, label: "Asset Class" },
    { value: TokenTimeseriesGroupBy.asset, label: "Asset" },
    { value: TokenTimeseriesGroupBy.assetTicker, label: "Asset Ticker" },
    { value: TokenTimeseriesGroupBy.token, label: "Token" },
    // { value: TokenTimeseriesGroupBy.measure, label: "Measure" },
    { value: TokenTimeseriesGroupBy.peggedAsset, label: "Pegged Asset" },
    {
        value: TokenTimeseriesGroupBy.jurisdictionCountry,
        label: "Jurisdiction Country",
    },
    {
        value: TokenTimeseriesGroupBy.realEstateCountry,
        label: "Real Estate Country",
    },
    { value: TokenTimeseriesGroupBy.auditor, label: "Auditor" },
    { value: TokenTimeseriesGroupBy.cryptoBroker, label: "Crypto Broker" },
    {
        value: TokenTimeseriesGroupBy.cryptoCustodian,
        label: "Crypto Custodian",
    },
    {
        value: TokenTimeseriesGroupBy.fundAdministrator,
        label: "Fund Administrator",
    },
    { value: TokenTimeseriesGroupBy.payingAgent, label: "Paying Agent" },
    { value: TokenTimeseriesGroupBy.subAdvisor, label: "Sub Advisor" },
    {
        value: TokenTimeseriesGroupBy.traditionalBroker,
        label: "Traditional Broker",
    },
    { value: TokenTimeseriesGroupBy.transferAgent, label: "Transfer Agent" },
    {
        value: TokenTimeseriesGroupBy.traditionalCustodian,
        label: "Traditional Custodian",
    },
];

// The same group-by options as TIMESERIES_GROUP_BY_OPTIONS, organized into
// labeled sections for the optgroup-style ComboboxV2 dropdown.
export const TIMESERIES_GROUP_BY_OPTION_GROUPS: ComboboxOptionGroup<string>[] =
    [
        {
            label: "Classification",
            options: [
                {
                    value: TokenTimeseriesGroupBy.assetClass,
                    label: "Asset Class",
                },
                { value: TokenTimeseriesGroupBy.issuer, label: "Issuer" },
                { value: TokenTimeseriesGroupBy.manager, label: "Manager" },
                { value: TokenTimeseriesGroupBy.network, label: "Network" },
                { value: TokenTimeseriesGroupBy.protocol, label: "Platform" },
                // { value: TokenTimeseriesGroupBy.baseProtocol, label: "Base Protocol" },
                // { value: TokenTimeseriesGroupBy.parentNetwork, label: "Parent Network" },
            ],
        },
        {
            label: "Asset",
            options: [
                { value: TokenTimeseriesGroupBy.asset, label: "Asset" },
                {
                    value: TokenTimeseriesGroupBy.assetTicker,
                    label: "Asset Ticker",
                },
                {
                    value: TokenTimeseriesGroupBy.assetType,
                    label: "Asset Type",
                },
                {
                    value: TokenTimeseriesGroupBy.peggedAsset,
                    label: "Pegged Asset",
                },
                {
                    value: TokenTimeseriesGroupBy.peggedAssetType,
                    label: "Pegged Asset Type",
                },
                { value: TokenTimeseriesGroupBy.token, label: "Token" },
            ],
        },
        {
            label: "Regulatory & Jurisdiction",
            options: [
                {
                    value: TokenTimeseriesGroupBy.jurisdictionCountry,
                    label: "Jurisdiction Country",
                },
                {
                    value: TokenTimeseriesGroupBy.realEstateCountry,
                    label: "Real Estate Country",
                },
            ],
        },
        {
            label: "Service Providers",
            options: [
                { value: TokenTimeseriesGroupBy.auditor, label: "Auditor" },
                {
                    value: TokenTimeseriesGroupBy.cryptoBroker,
                    label: "Crypto Broker",
                },
                {
                    value: TokenTimeseriesGroupBy.cryptoCustodian,
                    label: "Crypto Custodian",
                },
                {
                    value: TokenTimeseriesGroupBy.fundAdministrator,
                    label: "Fund Administrator",
                },
                {
                    value: TokenTimeseriesGroupBy.payingAgent,
                    label: "Paying Agent",
                },
                {
                    value: TokenTimeseriesGroupBy.subAdvisor,
                    label: "Sub Advisor",
                },
                {
                    value: TokenTimeseriesGroupBy.traditionalBroker,
                    label: "Traditional Broker",
                },
                {
                    value: TokenTimeseriesGroupBy.transferAgent,
                    label: "Transfer Agent",
                },
                {
                    value: TokenTimeseriesGroupBy.exchangeAgent,
                    label: "Exchange Agent",
                },
                {
                    value: TokenTimeseriesGroupBy.traditionalCustodian,
                    label: "Traditional Custodian",
                },
            ],
        },
    ];

export const TIMESERIES_INTERVAL_OPTIONS = [
    { value: AggregateQueryInterval.day, label: "Day" },
    { value: AggregateQueryInterval.week, label: "Week" },
    { value: AggregateQueryInterval.month, label: "Month" },
    { value: AggregateQueryInterval.quarter, label: "Quarter" },
    { value: AggregateQueryInterval.year, label: "Year" },
];

// export const TIMESERIES_AGGREGATE_FUNCTION_OPTIONS = [
//     { value: AggregateFunctions.Sum, label: "Sum" },
//     { value: AggregateFunctions.Avg, label: "Avg" },
//     { value: AggregateFunctions.Min, label: "Min" },
//     { value: AggregateFunctions.Max, label: "Max" },
// ];

export const TIMESERIES_MODE_OPTIONS = [
    {
        value: AggregateQueryMode.stock,
        label: "Stock",
        description:
            "Pick one reading per bucket (latest in-window). Use for gauges, balances, snapshots, point-in-time values.",
    },
    {
        value: AggregateQueryMode.flow,
        label: "Flow",
        description:
            "Aggregate all readings in each bucket. Use for counters, transactions, additive metrics, period-to-date values.",
    },
];

export const COMPOSITION_GROUP_LIMIT_OPTIONS = [5, 10, 15, 25, 50, 75, 100];

export const DEFAULT_COMPOSITION_GROUP_LIMIT = 15;

// // // //

export const DEFAULT_TIMESERIES_CONFIG: TimeseriesChartConfig = {
    measure_slug: MEASURE_SLUGS.bridged_token_value_dollar,
    default_group_by: TokenTimeseriesGroupBy.assetClass,
    groups_by_options: [],
    default_interval: AggregateQueryInterval.month,
    intervals_options: [],
    query: {
        filter: {
            operator: FilterOperators.And,
            filters: [],
        },
        aggregate: {
            groupBy: TokenTimeseriesGroupBy.assetClass,
            aggregateFunction: AggregateFunctions.Sum,
            interval: AggregateQueryInterval.month,
            groupLimit: 50,
            mode: AggregateQueryMode.stock,
        },
        sort: {
            direction: SortDirections.asc,
            field: TokenTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    },
};

export const DEFAULT_COMPOSITION_CONFIG: CompositionChartConfig = {
    measure_slug: MEASURE_SLUGS.bridged_token_value_dollar,
    group_limit: DEFAULT_COMPOSITION_GROUP_LIMIT,
    default_group_by: TokenTimeseriesGroupBy.assetClass,
    groups_by_options: [],
    query: {
        filter: {
            operator: FilterOperators.And,
            filters: [],
        },
        aggregate: {
            groupBy: TokenTimeseriesGroupBy.assetClass,
            aggregateFunction: AggregateFunctions.Sum,
            interval: AggregateQueryInterval.day,
            mode: AggregateQueryMode.stock,
            groupLimit: 50,
        },
        sort: {
            direction: SortDirections.asc,
            field: TokenTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    },
};

export { DEFAULT_NET_FLOWS_CONFIG } from "./flows/netFlowsConfig";

export { DEFAULT_LEAGUE_TABLE_CONFIG } from "./league_table/leagueTableConfig";

export { DEFAULT_ASSET_TABLE_CONFIG } from "./asset_table/assetTableConfig";
